/**
 * One ability, one text (plan 29 F2): the hotbar tooltip, the spellbook and
 * the trainer all describe an ability with its TOML description and the stat
 * line computed from content at the player's level
 * (src/shared/content/abilityText.ts).
 */
import { abilityDescription, abilityStatLine, type AbilityStat, type AbilityTextCaster } from '../../../shared/content/abilityText';
import type { TooltipData } from './Tooltip';

/** The client character as the stat line reads it. */
export function textCasterOf(player: {
  level?: number; class?: string; maxHealth?: number;
  stats?: object;
} | null | undefined): AbilityTextCaster {
  const stats = (player?.stats ?? {}) as { attack?: number; magicAttack?: number; intelligence?: number; maxHp?: number };
  return {
    level: player?.level,
    class: player?.class,
    maxHealth: player?.maxHealth,
    stats: { attack: stats.attack, magicAttack: stats.magicAttack, intelligence: stats.intelligence, maxHp: stats.maxHp },
  };
}

/** The ability's description: TOML first, then any registry text. */
export function abilityText(abilityId: string, fallback?: string): string {
  return abilityDescription(abilityId) || fallback || '';
}

export function abilityStats(abilityId: string, player: Parameters<typeof textCasterOf>[0]): AbilityStat[] {
  return abilityStatLine(abilityId, textCasterOf(player));
}

/** Tooltip data for an ability slot. */
export function abilityTooltipData(
  abilityId: string,
  player: Parameters<typeof textCasterOf>[0],
  opts: { title: string; fallbackDescription?: string; extraInfo?: string },
): TooltipData {
  return {
    title: opts.title,
    description: abilityText(abilityId, opts.fallbackDescription) || undefined,
    abilityStats: abilityStats(abilityId, player),
    extraInfo: opts.extraInfo,
  };
}
